import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, CheckCircle2, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/services/api";

function ReviewEmptyState() {
  return (
    <section className="neo-card surface-mint mt-8 p-6" data-testid="review-empty-state">
      <CheckCircle2 size={34} />
      <h2 className="mt-4 font-display text-3xl font-black">Nada vencido agora.</h2>
      <p className="mt-2 font-bold text-slate-600">Continue praticando Gramática e Cenários para gerar revisões úteis.</p>
      <Link className="primary-action mt-5 w-fit" to="/grammar" data-testid="review-empty-grammar-link">Praticar gramática</Link>
    </section>
  );
}

function ReviewCard({ answer, item, mutation, result, updateAnswer }) {
  const resultClass = result?.correct ? "grammar-score-panel mt-4" : "grammar-score-panel mt-4 auth-error";
  return (
    <article className="neo-card surface-neutral p-5" data-testid={`review-card-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid={`review-card-source-${item.id}`}>{item.source}</p>
          <h2 className="mt-2 font-display text-2xl font-black" data-testid={`review-card-prompt-${item.id}`}>{item.prompt_pt}</h2>
        </div>
        <span className="profile-badge" data-testid={`review-card-strength-${item.id}`}>força {item.strength}</span>
      </div>
      <p className="mt-4 text-lg font-black text-slate-800" data-testid={`review-card-question-${item.id}`}>{item.question}</p>
      <p className="mt-3 text-sm font-bold text-slate-600" data-testid={`review-card-tip-${item.id}`}>{item.tip_pt}</p>
      <input className="auth-input mt-4" value={answer || ""} onChange={(event) => updateAnswer(item.id, event.target.value)} placeholder="Digite sua resposta" data-testid={`review-answer-input-${item.id}`} />
      {result && (
        <div className={resultClass} data-testid={`review-result-${item.id}`}>
          {result.correct ? <CheckCircle2 size={22} /> : <RotateCcw size={22} />}
          <span>{result.message_pt}</span>
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" className="primary-action w-auto" disabled={!answer?.trim() || mutation.isPending} onClick={() => mutation.mutate({ itemId: item.id, answer })} data-testid={`review-submit-button-${item.id}`}>Responder <Brain size={18} /></button>
        <Link className="secondary-action" to={item.route} data-testid={`review-practice-link-${item.id}`}>Praticar origem</Link>
      </div>
    </article>
  );
}

function ReviewCards({ answers, items, mutation, results, updateAnswer }) {
  return (
    <section className="mt-8 grid gap-5 lg:grid-cols-2" data-testid="review-card-grid">
      {items.map((item) => <ReviewCard key={item.id} answer={answers[item.id]} item={item} mutation={mutation} result={results[item.id]} updateAnswer={updateAnswer} />)}
    </section>
  );
}

export default function Review() {
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});
  const { data: items = [], isLoading } = useQuery({ queryKey: ["review-items"], queryFn: api.getReviewItems });

  const mutation = useMutation({
    mutationFn: ({ itemId, answer }) => api.answerReviewItem(itemId, answer),
    onSuccess: (response) => {
      setResults((current) => ({ ...current, [response.item.id]: response }));
      queryClient.invalidateQueries({ queryKey: ["review-items"] });
      queryClient.invalidateQueries({ queryKey: ["recommendations"] });
    },
    onError: (error, variables) => {
      setResults((current) => ({ ...current, [variables.itemId]: { correct: false, message_pt: formatApiErrorDetail(error.response?.data?.detail) } }));
    },
  });

  const updateAnswer = (itemId, value) => setAnswers((current) => ({ ...current, [itemId]: value }));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="review-page">
      <section className="page-title-row" data-testid="review-title-section">
        <p className="section-kicker" data-testid="review-page-kicker">revisão inteligente</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="review-page-heading">Reforce o que quase escapou.</h1>
        <p className="max-w-3xl text-base font-semibold text-slate-600 md:text-lg" data-testid="review-page-description">
          Cards aparecem quando você erra exercícios ou dá respostas fracas em conversas. Acerte para reagendar.
        </p>
      </section>

      {isLoading && <div className="neo-card surface-neutral mt-8 p-6" data-testid="review-loading-state">Carregando revisão...</div>}
      {!isLoading && items.length === 0 && <ReviewEmptyState />}
      {!isLoading && items.length > 0 && <ReviewCards answers={answers} items={items} mutation={mutation} results={results} updateAnswer={updateAnswer} />}
    </div>
  );
}